import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Check, ArrowLeft, MapPin } from "lucide-react";
import api, { buyLink } from "@/lib/api";

export default function ParkDetail() {
  const { slug } = useParams();
  const [park, setPark] = useState(null);
  const [products, setProducts] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/parks/${slug}`).then((r) => setPark(r.data)).catch(() => setNotFound(true));
    api.get("/products", { params: { featured: true } }).then((r) => setProducts(r.data.slice(0, 3)));
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-display text-4xl mb-4">Park not found</h1>
        <Link to="/regulations" className="btn-accent">Back to map</Link>
      </div>
    );
  }

  if (!park) return <div className="max-w-7xl mx-auto px-6 py-24 text-muted-foreground">Loading…</div>;

  return (
    <div>
      <section className="border-b border-border">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-16">
          <Link to="/regulations" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8" data-testid="back-to-map">
            <ArrowLeft className="w-4 h-4" /> Back to regulations map
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            <div className="lg:col-span-3">
              <div className="label-eyebrow mb-3" style={{ color: park.type === "wildlife_refuge" ? "#2D5A4C" : park.type === "national_park" ? "#C95B3B" : "#8A8D86" }}>
                {park.type === "wildlife_refuge" ? "National Wildlife Refuge" : park.type === "national_park" ? "National Park" : "Fishing Site"}
              </div>
              <h1 className="font-display text-5xl md:text-6xl leading-[0.95] tracking-tight mb-4" data-testid="park-title">
                {park.name}
              </h1>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
                <MapPin className="w-4 h-4" /> {park.state}
              </div>
              <p className="text-lg leading-relaxed">{park.summary}</p>
              {park.effective_date && (
                <div className="mt-6 inline-block border border-foreground px-4 py-2 bg-[#E6E2D6]">
                  <span className="label-eyebrow">Effective</span>{" "}
                  <span className="font-mono font-bold text-lg ml-2">{park.effective_date}</span>
                </div>
              )}
            </div>
            <div className="lg:col-span-2">
              {park.image_url && (
                <div className="aspect-[4/5] w-full overflow-hidden border border-border">
                  <img src={park.image_url} alt={park.name} className="w-full h-full object-cover" />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-16 grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <div className="label-eyebrow mb-4">The rules</div>
            <h2 className="font-display text-3xl mb-8">What you need to know before you cast</h2>
            <ul className="space-y-4">
              {park.rules?.map((r, i) => (
                <li key={i} className="flex gap-3 border-b border-border pb-4">
                  <Check className="w-5 h-5 rule-tick flex-shrink-0 mt-1" strokeWidth={2} />
                  <span className="text-base leading-relaxed">{r}</span>
                </li>
              ))}
            </ul>
            {park.tips && (
              <div className="mt-10 p-6 bg-[#E6E2D6] border border-border">
                <div className="label-eyebrow mb-2 text-[#C95B3B]">Pro tip</div>
                <p className="text-base leading-relaxed">{park.tips}</p>
              </div>
            )}
          </div>

          <aside>
            <div className="label-eyebrow mb-4">Approved gear</div>
            <h3 className="font-display text-2xl mb-6">Fish it legally</h3>
            <div className="space-y-4">
              {products.map((p) => (
                <a key={p.id} href={buyLink(p.id, "park")} target="_blank" rel="noreferrer" className="card-flat flex gap-4 p-3" data-testid={`park-product-${p.id}`}>
                  <div className="w-20 h-20 bg-[#E6E2D6] overflow-hidden flex-shrink-0">
                    <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    <div className="font-display text-lg leading-tight">{p.name}</div>
                    <div className="text-xs text-muted-foreground mt-1">{p.price}</div>
                  </div>
                </a>
              ))}
            </div>
            <Link to="/gear" className="btn-ghost mt-6 w-full justify-center">View all gear</Link>
          </aside>
        </div>
      </section>
    </div>
  );
}
